import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import styled from '@emotion/styled';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Button, IconButton, TextField, Box, Typography,
  Pagination, MenuItem, Container, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, InputAdornment
} from '@mui/material';
import NewAnalysisModal from './../components/NewAnalysisModal';
import { Visibility, Delete } from '@mui/icons-material';
import { apiService } from './../services/api/ApiService';
import { useNavigate } from 'react-router-dom';
import DeletePrecedentConfirmationDialog from './../components/DeletePrecedentConfirmationDialog';
import { CircularProgress } from '@mui/material';
import LoadingSpinner from './../components/common/LoadingSpinner';


const RelevanceTag = styled.span<{ background: string; color: string; status?: string }>`
  background-color: ${(props) => props.background};
  color: ${(props) => props.color};
  font-size: 0.75rem;
  padding: 4px 12px;
  border-radius: 9999px;
  display: inline-block;
  cursor: ${(props) => (props.status === 'ERROR' ? 'pointer' : 'default')};

  ${(props) =>
    props.status === 'ERROR' &&
    `
      &:hover {
        background-color: #f44336;
        color: #fff;
        font-weight: bold;
        transition: all 0.2s ease-in-out;
      }
    `}
`;
interface ListPrecedentProps {
  setActiveItem: (item: string) => void;
}

const isTokenValid = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp;
    if (!exp) return false;
    const now = Math.floor(Date.now() / 1000);
    return exp > now;
  } catch (err) {
    return false;
  }
};


const ListPrecedent: React.FC<ListPrecedentProps> = ({ setActiveItem }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [precedents, setPrecedents] = useState<any[]>([]);
  const [filteredPrecedents, setFilteredPrecedents] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [selectedError, setSelectedError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPrecedentToDelete, setSelectedPrecedentToDelete] = useState<any | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [loading, setLoading] = useState(true);
const [error, setError] = useState<string | null>(null);


  const navigate = useNavigate();

  useEffect(() => {
  setActiveItem('Análisis de Precedentes');

  const token = sessionStorage.getItem('token');


  fetchPrecedents();
}, [setActiveItem]);

  useEffect(() => {
    setFilteredPrecedents(
      precedents.filter((precedent) =>
        precedent.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm, precedents]);






  const fetchPrecedents = async () => {
  setLoading(true);
  setError(null);
  try {
    const userProfile = localStorage.getItem('userProfile');
    const userData = userProfile ? JSON.parse(userProfile) : null;

    if (!userData) {
      throw new Error("No se encontró información del usuario.");
    }

    setUserRole(userData?.role || null);

    let precedentsData;
    if (userData.role === 'admin' || userData.role === 'Supervisor' ) {
      precedentsData = await apiService.getPrecedenteList();
    } else {
      precedentsData = await apiService.getPrecedenteList(userData.id);
    }

    setPrecedents(precedentsData);
  } catch (error: any) {
    console.error("Error fetching precedents:", error);
    setError("Error al cargar los datos.");
  } finally {
    setLoading(false);
  }
};

  const handleDeletePrecedent = async (precedent: any) => {
    try {
      await apiService.deletePrecedent(precedent.pdf_file_name);
      fetchPrecedents();
      setSnackbarMessage('Precedente eliminado correctamente');
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error deleting precedent:", error);
      setSnackbarMessage('Error al eliminar el precedente');
      setSnackbarOpen(true);
    }
  };

  const handleModalOpen = () => {
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleViewPrecedentOpen = (precedent: any) => {
    localStorage.setItem('selectedPrecedent', JSON.stringify(precedent));
    navigate(`/precedente/${precedent.pdf_file_name}`);
  };

  const handleShowError = (errorMessage: string) => {
    setSelectedError(errorMessage || "Ha ocurrido un error desconocido durante el procesamiento");
    setErrorModalOpen(true);
  };

  const handleCloseErrorModal = () => {
    setErrorModalOpen(false);
  };

  const handleUploadSuccess = () => {
    fetchPrecedents();
    handleModalClose();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PROCESSING':
        return { background: '#FFE082', color: '#FF8F00', label: 'En proceso' };
      case 'ERROR':
        return { background: '#FFCDD2', color: '#D32F2F', label: 'Error' };
      case 'COMPLETED':
        return { background: '#C6F6D5', color: '#2e7d32', label: 'Completado' };
      case 'IN_PROGRESS':
        return { background: '#BBDEFB', color: '#1976d2', label: 'En progreso' };
      case 'HIDDEN':
        return { background: '#E0E0E0', color: '#757575', label: 'Oculto' };
      default:
        return { background: '#F5F5F5', color: '#9E9E9E', label: 'Desconocido' };
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return "Fecha inválida";
    }
  };

  const handleChangePage = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const paginatedData = filteredPrecedents.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const currentPrecedentStart = (page - 1) * rowsPerPage + 1;
  const currentPrecedentEnd = Math.min(page * rowsPerPage, filteredPrecedents.length);











 return (
    <Container maxWidth={false} disableGutters sx={{ p: 3, backgroundColor: '#fff', minHeight: '100vh' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" sx={{ fontWeight: 600, fontSize: 40 }}>
          Análisis de Precedentes
        </Typography>
      </Box>

      <Typography variant="body2" color="textSecondary" paragraph>
        En esta sección, podrás ver todos los Análisis de precedentes que has realizado. Utiliza la barra de búsqueda para encontrar precedentes por nombre, número de expediente u otros criterios.
      </Typography>

      <Box display="flex" mb={4} gap={2}>
        <TextField
          fullWidth
          placeholder="Busca por título"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FontAwesomeIcon icon={faSearch} style={{ color: '#A0AEC0' }} />
              </InputAdornment>
            ),
          }}
        />
       {userRole !== 'admin' && userRole !== 'Supervisor' && (
          <Button
            variant="contained"
            sx={{ backgroundColor: '#002E74', color: '#fff', height: '56px' }}
            onClick={() => setModalOpen(true)}
          >
            Nuevo análisis
          </Button>
        )}
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="300px">
          <LoadingSpinner />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Título Análisis</TableCell>
                <TableCell>Número de expediente</TableCell>
                <TableCell>Precedentes</TableCell>
                <TableCell>Nombre del archivo PDF</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.length > 0 ? (
                paginatedData.map((precedent) => {
                  const statusColor = getStatusColor(precedent.status);
                  return (
                    <TableRow key={precedent.pdf_file_name}>
                      <TableCell>{precedent.name}</TableCell>
                      <TableCell>{precedent.numberCase}</TableCell>
                      <TableCell>{precedent.num_sentencias || 0}</TableCell>
                      <TableCell>{precedent.pdf_file_name}</TableCell>
                      <TableCell>{formatDate(precedent.createdAt)}</TableCell>
                      <TableCell>
                        <Tooltip title={precedent.status === 'ERROR' ? "Ha ocurrido un error" : ""}>
                          <span>
                            <RelevanceTag
                              background={statusColor.background}
                              color={statusColor.color}
                              status={precedent.status}
                            >
                              {statusColor.label}
                            </RelevanceTag>
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Ver análisis">
                          <IconButton
                            aria-label="Ver análisis"
                            onClick={precedent.status === 'ERROR' ? undefined : () => handleViewPrecedentOpen(precedent)}
                            disabled={precedent.status === 'ERROR'}
                          >
                            <Visibility sx={{ color: precedent.status === 'ERROR' ? 'gray' : 'black' }} />
                          </IconButton>
                        </Tooltip>
                        {userRole !== 'Supervisor' && (
                          <Tooltip title="Eliminar">
                            <IconButton
                              aria-label="Eliminar"
                              onClick={() => {
                                setSelectedPrecedentToDelete(precedent);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Delete sx={{ color: '#D32F2F' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">No se encontraron precedentes</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {filteredPrecedents.length > 0 && (
        <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
          <TextField
            select
            label="Filas por página"
            value={rowsPerPage}
            onChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
            variant="outlined"
            size="small"
          >
            {[10, 20, 30].map((option) => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </TextField>
          <Pagination
            count={Math.ceil(filteredPrecedents.length / rowsPerPage)}
            page={page}
            onChange={(e, value) => setPage(value)}
            variant="outlined"
            shape="rounded"
          />
          <Typography color="textSecondary">
            {Math.min((page - 1) * rowsPerPage + 1, filteredPrecedents.length)}-
            {Math.min(page * rowsPerPage, filteredPrecedents.length)} de {filteredPrecedents.length}
          </Typography>
        </Box>
      )}

      <NewAnalysisModal open={modalOpen} onClose={() => setModalOpen(false)} onUploadSuccess={fetchPrecedents} />

      <Dialog open={errorModalOpen} onClose={() => setErrorModalOpen(false)}>
        <DialogTitle sx={{ backgroundColor: '#FFCDD2', color: '#D32F2F' }}>
          <Box display="flex" alignItems="center">
            <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: 8 }} />
            <Typography variant="h6">Error en el procesamiento</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#D32F2F' }}>{selectedError}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorModalOpen(false)} sx={{ color: '#D32F2F' }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {selectedPrecedentToDelete && (
        <DeletePrecedentConfirmationDialog
          open={deleteDialogOpen}
          precedentName={selectedPrecedentToDelete.name}
          onClose={() => setDeleteDialogOpen(false)}
          onDelete={() => {
            handleDeletePrecedent(selectedPrecedentToDelete);
            setDeleteDialogOpen(false);
            setSelectedPrecedentToDelete(null);
          }}
        />
      )}
    </Container>
  );
};

export default ListPrecedent;
